import type { ReactElement } from "react";

import { FormSummary } from "../../forms/fields.js";
import { useApiForm } from "../../forms/use-api-form.js";

import { adminCenterApi } from "./urls.js";

/**
 * 送る直前に聞く文（重要な操作は最後にダイアログで確かめる。docs/01 §7）。
 * <b>招待メールが届くことと、登録が済むまで契約者には選べないこと</b>を言う。
 */
export const inviteUserDialog = (title: string, email: string, admin: boolean): string =>
    `「${title}」に「${email}」を${admin ? "管理者として" : ""}招待します。`
    + "運営事務局からの招待メールが届き、ご本人が登録すると利用者になります（登録が済むまで契約者には選べません）。よろしいですか？";

/**
 * 利用者を招待する（Implem.Portal.Backend の docs/features/35 §2）。契約 1 件の画面の利用者の区画に置く。
 * <b>アカウント管理を持つ運営に、解約していない契約でだけ出す</b>（<c>canInvite</c>。サーバが決める）。
 *
 * 契約者が退職し、ほかに管理者も居ない契約で、新しい契約者にする人を契約に入れるためのもの
 * （招待 → 本人が登録 → 「契約者を変更する」）。欄の名前は会員の画面の招待と同じ（<c>InviteViewModel</c>）。
 * 送ると契約 1 件の画面へ戻り、知らせが出る（行き先と文はサーバが返す）。
 */
export function TenantInvite({ tenantId, title }: {
    readonly tenantId: number;
    /** 契約のシステム名（確認ダイアログの文に入れる）。 */
    readonly title: string;
}): ReactElement {
    const form = useApiForm(adminCenterApi.inviteUser(tenantId), undefined, (valueOf) => inviteUserDialog(
        title, valueOf("Email").trim(), valueOf("Admin") === "true",
    ));
    const emailError = form.fieldError("Email");

    return (
        <div className="mb-4 border-b border-slate-200 pb-4" data-invite-user="">
            <h3 className="mb-2 text-sm font-semibold">利用者を招待する</h3>
            <p className="mb-3 text-sm text-slate-500">
                契約者が居なくなり、ほかに管理者も居ないときに使います。新しい契約者にする方を招待し、登録が済んだら「契約者を変更する」で選びます。
            </p>
            <form method="post" onSubmit={form.onSubmit}>
                <FormSummary messages={form.summary} />
                <div className="flex flex-wrap items-end gap-2">
                    <div className="min-w-0 grow">
                        <label className="form-label" htmlFor="inviteEmail">メールアドレス</label>
                        <input
                            className={emailError === undefined ? "form-input" : "form-input is-invalid"}
                            id="inviteEmail" name="Email" type="email" placeholder="mail@example.com" required autoComplete="off"
                            aria-invalid={emailError === undefined ? undefined : true}
                        />
                    </div>
                    <button type="submit" className="btn-secondary" disabled={form.busy}>招待メールを送信</button>
                </div>
                {emailError === undefined
                    ? null
                    : <span className="form-error field-validation-error" data-valmsg-for="Email">{emailError}</span>}
                {/* 外すと送られず、一般の利用者として招待する。新しい契約者にする方は管理者に（/fs でもテナントの管理者で作られる） */}
                <label className="mt-2 flex items-center gap-2 text-sm">
                    <input className="form-check" name="Admin" type="checkbox" value="true" defaultChecked />
                    <span>管理者として招待する</span>
                </label>
            </form>
        </div>
    );
}
